import * as React from 'react';
import { useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';

import { SegmentSearchParameter } from '@websky/graphql';
import { useTheme } from '../../../../../../theme';
import { useToggleable } from '../../../../../../hooks';
import { format, MOBILE_MIN_WIDTH, TABLET_MIN_WIDTH } from '../../../../../../utils';
import { BaseButton, Header, ModalX } from '../../../../../../BaseComponents';
import { useAvailableDates } from '../../../../../../Modules/Exchange/components/Exchange/hooks';
import DatepickerComponent from '../../../../../../Modules/Exchange/components/Exchange/SearchFlights/Calendar/Datepicker/Datepicker';

import { CdnIcon } from '@shared/ui';

interface Props {
	className?: string;
	date?: Date;
	minDate: Date;
	selectedDates?: Date[];
	segmentSearchParams?: SegmentSearchParameter;
	isOpen?: boolean;
	isSelected: boolean;
	onSelectDate: (data: Date) => void;
	renderTrigger?: (onOpen: () => void, isOpen: boolean) => React.ReactNode;
}

const SelectDate: React.FC<Props> = ({
	date,
	minDate,
	selectedDates,
	segmentSearchParams,
	isOpen,
	isSelected,
	onSelectDate,
	renderTrigger
}) => {
	const { t } = useTranslation('Exchange');
	const { Segments: segmentsTheme } = useTheme('Widgets').Exchange;

	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });
	const isTablet = useMediaQuery({ maxWidth: TABLET_MIN_WIDTH });

	const { dates } = useAvailableDates(segmentSearchParams);
	const { isOpen: isOpenDatepicker, open, close } = useToggleable(false);

	useEffect(() => {
		if (isOpen) {
			open();
		}
	}, [isOpen]);

	const onSelectDateHandler = (date: Date) => {
		close();
		onSelectDate(date);
	};

	return (
		<>
			{renderTrigger?.(open, isOpen) ?? (
				<BaseButton
					variant="optionAction"
					className={segmentsTheme.button}
					disabled={!isSelected}
					startIcon={<CdnIcon icon="Action/Show Calendar" />}
					onClick={open}
				>
					{date ? format(date, 'dd MMM') : t('Datepicker.SelectDate')}
				</BaseButton>
			)}

			<ModalX
				classes={{ paper: segmentsTheme.paper }}
				size={isMobile ? 'fullscreen' : 'xlarge'}
				title={
					<Header
						className={segmentsTheme.header}
						left={<span className={segmentsTheme.title}>{t('Select new date')}</span>}
						showEmptySlots={false}
						equalSlotsBasis={false}
						onGoBackward={close}
					/>
				}
				noPadding
				isOpen={isOpenDatepicker}
				onClose={close}
			>
				<DatepickerComponent
					minDate={minDate}
					openDate={minDate ?? date}
					monthCount={isTablet ? (isMobile ? 12 : 2) : 3}
					monthsToScroll={isTablet ? 2 : 3}
					selectedDates={selectedDates ?? []}
					availableDates={dates ?? []}
					onSelect={onSelectDateHandler}
				/>
			</ModalX>
		</>
	);
};

export default SelectDate;
